import enPaywallV8 from "../locales/paywalls/paywall-v8/en.json";
import esPaywallV8 from "../locales/paywalls/paywall-v8/es.json";
import enPaywallV9 from "../locales/paywalls/paywall-v9/en.json";
import esPaywallV9 from "../locales/paywalls/paywall-v9/es.json";
import enPaywalls from "../locales/paywalls/en.json";
import esPaywalls from "../locales/paywalls/es.json";
import dePaywalls from "../locales/paywalls/de.json";
import frPaywalls from "../locales/paywalls/fr.json";
import itPaywalls from "../locales/paywalls/it.json";
import itPaywallV8 from "../locales/paywalls/paywall-v8/it.json";
import itPaywallV9 from "../locales/paywalls/paywall-v9/it.json";
import itOnboardings from "../locales/onboardings/it.json";
import jaPaywalls from "../locales/paywalls/ja.json";
import ptBrPaywalls from "../locales/paywalls/pt_br.json";
import arPaywalls from "../locales/paywalls/ar.json";
import enOnboardings from "../locales/onboardings/en.json";
import esOnboardings from "../locales/onboardings/es.json";
import enApp from "../locales/app/en/app.json";
import esApp from "../locales/app/es/app.json";
import deApp from "../locales/app/de/app.json";
import frApp from "../locales/app/fr/app.json";
import itApp from "../locales/app/it/app.json";
import jaApp from "../locales/app/ja/app.json";
import ptBrApp from "../locales/app/pt_br/app.json";
import arApp from "../locales/app/ar/app.json";
import { resolveLanguageCode } from "./resolve-language";
import { UNRELEASED_LANGUAGES } from "./unreleased";

export enum Language {
    EN = "en",
    ES = "es",
    DE = "de",
    FR = "fr",
    IT = "it",
    JA = "ja",
    PT_BR = "pt_br",
    AR = "ar"
}

/**
 * Languages i18next operates with. Extended as the business adds more translations.
 */
export const SUPPORTED_LANGUAGES = Object.values(Language) as string[];

// Caveat carried forward: the prompt library has no pt_br/ar rows yet, so it
// still comes back in English.

/**
 * Languages offered in the language picker. A subset of SUPPORTED_LANGUAGES:
 * a locale lands here only once its copy is signed off.
 */
export const PICKER_LANGUAGES = SUPPORTED_LANGUAGES.filter((lng) => !UNRELEASED_LANGUAGES.includes(lng));

/**
 * App namespace for natural keys (English text = key).
 * Funnel namespaces (commonPaywall/paywallV8/V9/onboarding) live separately
 * and are left untouched.
 */
export const APP_NS = "app";

export const LANGUAGE_COOKIE = "i18nextLng";

export const I18N_NAMESPACES = ["app", "commonPaywall", "paywallV8", "paywallV9", "onboarding"] as const;

/**
 * Shared resource bundle used by both the client (react-i18next) instance and
 * the server-only instance. Kept here, free of any React import, so it can be
 * pulled into React Server Components without crashing.
 */
export const resources = {
    en: {
        app: enApp,
        paywallV8: enPaywallV8,
        paywallV9: enPaywallV9,
        commonPaywall: enPaywalls,
        onboarding: enOnboardings
    },
    es: {
        app: esApp,
        paywallV8: esPaywallV8,
        paywallV9: esPaywallV9,
        commonPaywall: esPaywalls,
        onboarding: esOnboardings
    },
    de: {
        app: deApp,
        commonPaywall: dePaywalls
    },
    fr: {
        app: frApp,
        commonPaywall: frPaywalls
    },
    // Italian is the one late locale with the funnel namespaces translated too, so
    // nothing falls back to en.
    it: {
        app: itApp,
        paywallV8: itPaywallV8,
        paywallV9: itPaywallV9,
        commonPaywall: itPaywalls,
        onboarding: itOnboardings
    },
    ja: {
        app: jaApp,
        commonPaywall: jaPaywalls
    },
    // commonPaywall carries only the subscriptionOption block so far — the rest of
    // the funnel namespaces (paywallV8/V9, onboarding) are managed separately and
    // still fall back to en for these two locales.
    pt_br: {
        app: ptBrApp,
        commonPaywall: ptBrPaywalls
    },
    ar: {
        app: arApp,
        commonPaywall: arPaywalls
    }
};

export const normalizeLanguage = (value?: string | null): string | null =>
    resolveLanguageCode(value, SUPPORTED_LANGUAGES);

/**
 * Locales written right-to-left. The whole app renders with `<html dir="rtl">`
 * for them: the root layout sets it from the language cookie before first paint
 * and i18n/index.ts keeps it in sync on every language change.
 */
export const RTL_LANGUAGES: string[] = [Language.AR];

export const isRtlLanguage = (value?: string | null): boolean => {
    const normalized = normalizeLanguage(value);
    return normalized !== null && RTL_LANGUAGES.includes(normalized);
};

/** `dir` value for content in the given language; unknown/missing → "ltr". */
export const getLanguageDirection = (value?: string | null): "ltr" | "rtl" => (isRtlLanguage(value) ? "rtl" : "ltr");

/**
 * Inline script for the root layout's <head>: sets `<html dir="rtl">` before the
 * first paint, since i18next only learns the language after hydration. It
 * resolves the language in the order detectInitialLanguage (i18n/index.ts) uses —
 * `?lang=`, then the cookie, then navigator.language against the picker list —
 * so a `?lang=en` link over an `ar` cookie and a first visit without a cookie
 * both paint in the right direction.
 */
export const HTML_DIR_SCRIPT = `(function(){var S=${JSON.stringify(SUPPORTED_LANGUAGES)},P=${JSON.stringify(PICKER_LANGUAGES)},R=${JSON.stringify(RTL_LANGUAGES)};function n(v,a){if(!v)return null;var l=String(v).trim().toLowerCase();if(!l)return null;var e=l.replace(/-/g,"_");if(a.indexOf(e)!==-1)return e;var b=e.split("_")[0];if(a.indexOf(b)!==-1)return b;for(var i=0;i<a.length;i++)if(a[i].indexOf(b+"_")===0)return a[i];return null}var q=null;try{q=new URLSearchParams(location.search).get("lang")}catch(x){}var c=document.cookie.match(/(?:^|; )${LANGUAGE_COOKIE}=([^;]*)/);var l=n(q,S)||n(c&&decodeURIComponent(c[1]),S)||n(navigator.language,P)||"en";if(R.indexOf(l)!==-1)document.documentElement.dir="rtl"})();`;

export const normalizeToPickerLanguage = (value?: string | null): string | null =>
    resolveLanguageCode(value, PICKER_LANGUAGES);

/**
 * BCP-47 tag for `<html lang>`: our codes carry the region behind an underscore
 * and in lower case (`pt_br`), HTML wants `pt-BR`.
 */
export const toHtmlLang = (value: string): string => {
    const [base, region] = value.split("_");
    return region ? `${base}-${region.toUpperCase()}` : base;
};
